export const STORY_PIXELS_PER_UNIT = 124;
export const STORY_VISIBLE_RADIUS = 5.4;

const MAX_SIDE_SHIFT = 64;
const SCALE_FALLOFF = 3.4;
const OPACITY_FALLOFF = 4.1;

export function applyStoryTransforms(
  container: HTMLElement | null,
  cameraPosition: number,
  reducedMotion: boolean,
): void {
  if (!container) return;
  const nodes = container.querySelectorAll<HTMLElement>('[data-story-position]');
  for (const node of nodes) {
    const rawPosition = node.dataset.storyPosition;
    if (!rawPosition) continue;
    const itemPosition = Number(rawPosition);
    if (!Number.isFinite(itemPosition)) continue;
    const itemIndex = Number(node.dataset.storyIndex ?? '0');
    const distance = itemPosition - cameraPosition;
    const y = distance * STORY_PIXELS_PER_UNIT;
    const motion = createCardMotion(distance, itemIndex, reducedMotion);
    const isActive = node.dataset.storyActive === 'true';
    // -50% sentrerer kortet om ankeret (left/top 50%); inline transform ville
    // ellers overstyre Tailwind-klassene som skulle gjort det samme.
    node.style.transform = `translate3d(calc(-50% + ${motion.x.toFixed(2)}px), calc(-50% + ${y.toFixed(2)}px), 0) scale(${motion.scale.toFixed(3)})`;
    node.style.opacity = motion.opacity.toFixed(3);
    node.style.zIndex = String(isActive ? 5000 : motion.zIndex);
  }
}

function createCardMotion(distance: number, itemIndex: number, reducedMotion: boolean) {
  if (reducedMotion) {
    const opacity = clamp(1 - Math.abs(distance) * 0.22, 0.2, 1);
    return { x: 0, scale: 1, opacity, zIndex: zFromDistance(distance) };
  }
  const distanceAbs = Math.abs(distance);
  const attenuation = Math.exp(-Math.pow(distanceAbs / SCALE_FALLOFF, 2));
  const side = itemIndex % 2 === 0 ? -1 : 1;
  const wave = Math.sin((itemIndex + distance * 0.8) * 0.85);
  const x = side * wave * distanceAbs * 8.5 + side * (1 - attenuation) * MAX_SIDE_SHIFT * 0.6;
  const scale = 0.76 + attenuation * 0.34;
  const opacity = clamp(
    0.16 + Math.exp(-Math.pow(distanceAbs / OPACITY_FALLOFF, 1.25)) * 0.88,
    0.16,
    1,
  );
  return { x, scale, opacity, zIndex: zFromDistance(distance) };
}

function zFromDistance(distance: number): number {
  return Math.max(1, 2000 - Math.round(Math.abs(distance) * 100));
}

export function formatStoryDateTime(epochMs: number): string {
  if (!Number.isFinite(epochMs)) return '-';
  return new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(epochMs));
}

export function formatStoryDay(epochMs: number): string {
  if (!Number.isFinite(epochMs)) return '-';
  return new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'full',
    timeZone: 'UTC',
  }).format(new Date(epochMs));
}

export function dayKey(epochMs: number): string {
  if (!Number.isFinite(epochMs)) return '-';
  return new Date(epochMs).toISOString().slice(0, 10);
}

export function blendPeriodTint(colors: readonly string[], reducedMotion: boolean): string {
  if (colors.length === 0) return 'transparent';
  const alpha = reducedMotion ? 0.1 : 0.16;
  if (colors.length === 1) {
    return hexToRgba(colors[0], alpha);
  }
  const [r, g, b] = mixHexColors(colors);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

export function periodAccentRgb(colors: readonly string[]): [number, number, number] {
  if (colors.length === 0) return [99, 102, 241];
  if (colors.length === 1) return parseHex(colors[0]);
  return mixHexColors(colors);
}

function mixHexColors(colors: readonly string[]): [number, number, number] {
  let red = 0;
  let green = 0;
  let blue = 0;
  for (const color of colors) {
    const [r, g, b] = parseHex(color);
    red += r;
    green += g;
    blue += b;
  }
  const count = Math.max(1, colors.length);
  return [Math.round(red / count), Math.round(green / count), Math.round(blue / count)];
}

function parseHex(color: string): [number, number, number] {
  const raw = color.startsWith('#') ? color.slice(1) : color;
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return [99, 102, 241];
  return [
    Number.parseInt(raw.slice(0, 2), 16),
    Number.parseInt(raw.slice(2, 4), 16),
    Number.parseInt(raw.slice(4, 6), 16),
  ];
}

function hexToRgba(color: string, alpha: number): string {
  const [r, g, b] = parseHex(color);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
